import { SlWapiService } from './../model/api-services/sl-wapi.service';
import { Injectable } from '@angular/core';
import { UUID } from 'angular2-uuid';

export type SBExternalMessage = {
  name: string,
  src: string,
  type: string,
  content: any,
  url?: string,
  replyTo?: string
}

export type SLMapMessage = SBExternalMessage & {
  type: 'sl-map-message'
}
export interface IExternalMessageReceiver {
  HandleExternalMessage?(message: SBExternalMessage): void;
}

@Injectable({
  providedIn: 'root'
})
export class SandboxMessengerService {
  private _id: string;
  private _appURL: string;
  private _key: CryptoKey;
  private _app: IExternalMessageReceiver;

  constructor() {
    this.SetKey();
  }
  private NormalizeURL(url?: string): string {
    if (!url) return '';
    try {
      const normalized = new URL(url, window.location.origin);
      normalized.hash = '';
      normalized.search = '';
      const base = normalized.origin + normalized.pathname.replace(/\/+$/, '');
      return base + '/';
    } catch (_err) {
      const base = ('' + url).split('#')[0].split('?')[0].replace(/\/+$/, '');
      return base + '/';
    }
  }
  private async SetKey() {
    this._key = await window.crypto.subtle.generateKey(
      {
        name: "AES-GCM",
        length: 256, // Can be 128, 192, or 256 bits
      },
      true, // Whether the key is extractable
      ["encrypt", "decrypt"] // Can be used to encrypt and decrypt
    );
  }
  private async CreateHash(input, length) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    return hash.slice(0, length);

  }

  private async Encrypt(text, ivRaw: string) {
    const iv = await this.CreateHash(ivRaw, 16); // Generate a 16-byte IV

    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const encrypted = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      this._key,
      data
    );
    let binary = '';
    const bytes = new Uint8Array(encrypted);
    const length = bytes.byteLength;
    for (let i = 0; i < length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  private async Decrypt(encryptedData: string, ivRaw: string) {
    const iv = await this.CreateHash(ivRaw, 16); // Generate a 16-byte IV
    const binaryString = window.atob(encryptedData); // Decode Base64 string to binary
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      this._key,
      bytes.buffer
    );
    let text = '';
    const outBytes = new Uint8Array(decrypted);
    const length = outBytes.byteLength;
    for (let i = 0; i < length; i++) {
      text += String.fromCharCode(outBytes[i]);
    }
    return text;
  }

  public Register(appURL: string, registerMsg: string = 'register-map', autoRegister: boolean = true) {
    if (!SlWapiService.SLSESSION) return;
    if (!this._id) this._id = UUID.UUID();
    if (!this._appURL) this._appURL = this.NormalizeURL(appURL);
    window.addEventListener('message', (message: any) => {
      // console.log('Message sent to sandbox messenger', message);

      if (typeof message.data !== 'string') return;
      const msg: any = JSON.parse(message.data);
      if (msg?.type == this.messageType) {
        this.HandleMessage(msg as SBExternalMessage);
      }
    });
    if (autoRegister) {
      this.SendMessage(this.MakeMessage(registerMsg, {}));
    }
  }
  private messageType: string;
  public RegisterHandler(app: IExternalMessageReceiver, messageType: string = 'sl-map-message') {

    if (!app.HandleExternalMessage) return;
    this.messageType = messageType;
    if (!this._id) this._id = UUID.UUID();

    this._app = app;
  }


  public MakeMessage(name: string, data: any, type?: string): SBExternalMessage {
    if (!type) type = this.messageType;
    const message: SBExternalMessage = {
      name: (name),
      src: this._id,
      type: (type),
      content: data
    }
    return message;
  }
  public HandleMessage(message: SBExternalMessage) {
    // console.log(message, message.src, this.messageType, this._id);

    if (message.type !== this.messageType) return;
    if (!this._app) return;

    // Accept messages *not* from this visual instance
    if (message.src === this._id) return;

    // Normalize URL only if provided
    const messageURL = this.NormalizeURL(message.url);

    switch (message.name) {
      case 'map-registered':
        this.HandleAppRegistered(message);
        break;
    }

    // Only forward messages if URL matches expected app URL
    // console.log('Comparing', messageURL, 'to', this._appURL);
    if (!message.url || messageURL === this._appURL) {
      // console.log("APP=", this._app);
      // console.log("HAS FUNC=", this._app?.HandleExternalMessage);
      this._app.HandleExternalMessage(message)
    }
  }
  public HandleAppRegistered(message: SBExternalMessage) {
    // console.log('SandboxMessagerService', message);
    this.SendMessage(this.MakeMessage('app-registered', {}));
  }
  SendMessage(message: SBExternalMessage) {
    // console.log(JSON.stringify(message));
    window.parent.postMessage(JSON.stringify(message), '*');
  }

}
